import { Injectable } from "@angular/core";
import { type } from "os";
import { BehaviorSubject, from, Observable, of } from "rxjs";
import { map, switchMap, take, tap } from "rxjs/operators";
import { Product } from "src/app/model/price/product";
import { PriceService } from "../price/price.service";

@Injectable({
  providedIn: "root",
})
export class ShopService {
  productList;
  currentProductList = new BehaviorSubject<Product[]>([]);
  filteredProductList = new BehaviorSubject<Product[]>(null);
  showFilterSide = new BehaviorSubject<boolean>(true);

  constructor(private priceService: PriceService) {
    this.refreshCurrentList();
  }

  refreshCurrentList() {
    this.productList = this.priceService.publishedProductList.value
    this.currentProductList.next(this.productList);
  }

  search(searchValue: string) {
    return this.priceService.publishedProductList.pipe(
      switchMap((productList: Product[]) =>
        of(
          productList?.filter(
            (product) =>
              !searchValue ||
              searchValue.length == 0 ||
              product.name.toLowerCase().includes(searchValue.toLowerCase())
          )
        )
      ),
      tap((filteredProductlist) =>
        this.currentProductList.next(filteredProductlist)
      )
    );
  }

  order(increasing: boolean = false) {
    return this.currentProductList.pipe(
      map((products: Product[]) =>
        products?.sort((product1, product2) =>
          increasing ? product1.buy - product2.buy : product2.buy - product1.buy
        )
      ),
      take(1),

      tap((list) => {
        this.currentProductList.next(list);
      })
    );
  }

  filterByType(typeList: string[]): Observable<Product[]> {
    if (!typeList || typeList.length == 0) {
      return from(this.currentProductList);
    }
    return of(this.productList).pipe(
      take(1),
      map((currentList) =>
        currentList.filter((product: Product) => {
          const metalTypeList = product.metalList.map(
            (metal) => metal.metalType
          );

          return (
            metalTypeList.every((metal) => typeList.includes(metal)) ||
            typeList.includes(product.productType)
          );
        })
      ),
      tap((list) => {
        this.currentProductList.next(list);
      })
    );
  }

  filterByAmount(minAmount: number, maxAmount: number, gram: boolean) {
    if (minAmount == null && maxAmount == null)
      return of(this.currentProductList);
    let min = minAmount || 0;
    let max = maxAmount || Number.MAX_VALUE;

    if (gram) {
      return this.currentProductList.pipe(
        map((productList: Product[]) =>
          productList.filter((product) => {
            const accumulatedResult = product.metalList.reduce(
              (acc, val) => acc + val.weight,
              0
            );
            return min < accumulatedResult && max > accumulatedResult;
          })
        ),
        take(1),
        tap((filteredProductList) =>
          this.currentProductList.next(filteredProductList)
        )
      );
    } else {
      return this.currentProductList.pipe(
        map((productList) =>
          productList.filter((product: Product) => {
            return min < product.buy && max > product.buy;
          })
        ),
        take(1),
        tap((filteredProducts) =>
          this.currentProductList.next(filteredProducts)
        )
      );
    }
  }
}
